import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useNavigate } from 'react-router-dom';
import { LIST_MY_INQUIRIES } from '../graphql/clientQueries';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import type { Inquiry, InquiryStatus, StaffMember } from '../types';

/**
 * Status configuration for inquiry status badges
 */
const INQUIRY_STATUS_CONFIG: Record<InquiryStatus, { label: string; color: string; bgColor: string }> = {
  NEW: { label: 'New', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  REVIEWING: { label: 'Under Review', color: 'text-amber-700', bgColor: 'bg-amber-100' },
  ACCEPTED: { label: 'Converted to Case', color: 'text-green-700', bgColor: 'bg-green-100' },
  REJECTED: { label: 'Closed', color: 'text-gray-700', bgColor: 'bg-gray-100' },
};

interface ListMyInquiriesResponse {
  listMyInquiries: {
    items: Inquiry[];
    nextToken: string | null;
  };
}

/**
 * InquiryScreen - Client inquiry list and details view
 * 
 * Displays all inquiries for the authenticated user including:
 * - List of all inquiries with status badges
 * - Expandable detail view for each inquiry
 * - Legal disclaimer about attorney-client relationship
 * - Pull-to-refresh functionality
 * - Button to submit new inquiry
 * 
 * Requirements: 3.5
 */
export function InquiryScreen() {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedInquiryId, setSelectedInquiryId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<ListMyInquiriesResponse>(LIST_MY_INQUIRIES, {
    fetchPolicy: 'cache-and-network',
  });

  // Filter out ACCEPTED inquiries - they've been converted to cases
  const allInquiries = data?.listMyInquiries?.items || [];
  const inquiries = allInquiries.filter(i => i.status !== 'ACCEPTED');
  const selectedInquiry = selectedInquiryId 
    ? inquiries.find(i => i.id === selectedInquiryId) 
    : inquiries[0];

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [refetch]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatShortDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusConfig = (status: string) => {
    return INQUIRY_STATUS_CONFIG[status as InquiryStatus] || { 
      label: status, 
      color: 'text-gray-700', 
      bgColor: 'bg-gray-100' 
    };
  };

  const handleNewInquiry = () => {
    navigate('/inquiry');
  };

  // Loading state
  if (loading && !data) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-aalaw-red mb-4"></div>
        <p className="text-gray-500">Loading your inquiries...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">😕</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load inquiries</h2>
        <p className="text-gray-600 mb-4">{error.message}</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  // Empty state - no inquiries
  if (inquiries.length === 0) {
    return (
      <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-4">📋</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Inquiries Yet</h2>
        <p className="text-gray-600 text-center mb-6">
          You haven't submitted any inquiries yet. Start by telling us about your case.
        </p>
        <Button 
          onClick={handleNewInquiry}
          style={{ backgroundColor: '#C41E3A', color: '#FFFFFF' }}
          className="hover:bg-red-700"
        >
          Submit New Inquiry
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">My Inquiries</h1>
            <p className="text-sm text-gray-500">{inquiries.length} {inquiries.length === 1 ? 'inquiry' : 'inquiries'}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <span className="animate-spin mr-2">↻</span>
            ) : (
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
        {/* Inquiry List - Left Panel */}
        <div className={`${inquiries.length > 1 ? 'lg:w-1/3 lg:border-r border-gray-200' : 'hidden'} overflow-y-auto`}>
          <div className="p-4 space-y-3">
            {inquiries.map((inquiry) => {
              const statusConfig = getStatusConfig(inquiry.status);
              const isSelected = selectedInquiry?.id === inquiry.id;
              
              return (
                <button
                  key={inquiry.id}
                  onClick={() => setSelectedInquiryId(inquiry.id)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    isSelected 
                      ? 'border-aalaw-red bg-red-50 shadow-sm' 
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm">{inquiry.incidentType}</h3>
                    <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0 text-xs`}>
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">
                    Submitted {formatShortDate(inquiry.createdAt)}
                  </p>
                  {inquiry.assignedCaseManager && (
                    <p className="text-xs text-gray-600 mt-1">
                      Manager: {inquiry.assignedCaseManager.firstName} {inquiry.assignedCaseManager.lastName}
                    </p>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Inquiry Detail - Right Panel */}
        <div className={`flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain scrollbar-visible ${inquiries.length === 1 ? 'w-full' : ''}`} style={{ WebkitOverflowScrolling: 'touch' }}>
          {selectedInquiry ? (
            <InquiryDetail 
              inquiry={selectedInquiry} 
              formatDate={formatDate} 
              getStatusConfig={getStatusConfig} 
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              Select an inquiry to view details
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/**
 * InquiryDetail - Displays detailed information for a single inquiry
 */
function InquiryDetail({ 
  inquiry, 
  formatDate, 
  getStatusConfig 
}: { 
  inquiry: Inquiry; 
  formatDate: (date?: string) => string;
  getStatusConfig: (status: string) => { label: string; color: string; bgColor: string };
}) {
  const statusConfig = getStatusConfig(inquiry.status);
  const caseManager = inquiry.assignedCaseManager;

  return (
    <div className="space-y-6">
      {/* Legal Disclaimer Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm text-amber-800">
          <span className="font-semibold">Important Notice: </span>
          No attorney-client relationship has been established. Attorney-client privilege 
          does not apply until a formal engagement agreement is signed and your case is 
          accepted by our firm.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inquiry Overview Card */}
        <Card className="lg:col-span-2">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Inquiry Overview</h2>
              <Badge className={`${statusConfig.bgColor} ${statusConfig.color} border-0`}>
                {statusConfig.label}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-500">Incident Type</span>
                <span className="font-medium text-gray-900">{inquiry.incidentType}</span>
              </div>

              <div className="flex justify-between items-center py-3 border-b border-gray-100">
                <span className="text-gray-500">Submitted</span>
                <span className="font-medium text-gray-900">{formatDate(inquiry.createdAt)}</span>
              </div>

              {inquiry.incidentDate && (
                <div className="flex justify-between items-center py-3 border-b border-gray-100">
                  <span className="text-gray-500">Incident Date</span>
                  <span className="font-medium text-gray-900">{formatDate(inquiry.incidentDate)}</span>
                </div>
              )}
            </div>

            {inquiry.description && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <span className="text-gray-500 block mb-2">Description</span>
                <p className="text-gray-900 leading-relaxed">{inquiry.description}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Inquiry Manager Card */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Your Inquiry Manager</h2>

            {caseManager ? (
              <div>
                <InquiryManagerCard member={caseManager} />
                
                <div className="mt-6 bg-gray-50 rounded-lg p-4 flex items-start gap-3">
                  <svg className="w-5 h-5 text-aalaw-red flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-sm text-gray-600">
                    Your inquiry manager is here to help guide you through the process. 
                    Use the Messages tab to communicate directly with them.
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center py-8 text-center">
                <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="text-gray-500 italic">
                  An inquiry manager will be assigned to your inquiry shortly.
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* What's Next Card */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-6">What's Next?</h2>

            <div className="flex flex-col items-center py-4 text-center">
              <svg className="w-10 h-10 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 leading-relaxed">
                Your inquiry is being actively processed. You can now message your 
                inquiry manager and upload documents.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

/**
 * InquiryManagerCard - Displays assigned inquiry manager with contact info
 */
function InquiryManagerCard({ member }: { member: StaffMember }) {
  const fullName = member.firstName && member.lastName 
    ? `${member.firstName} ${member.lastName}`
    : 'Inquiry Manager';

  const initials = member.firstName 
    ? member.firstName[0].toUpperCase()
    : member.email?.[0]?.toUpperCase() || '?';

  const handleCall = () => {
    if (member.phoneNumber) {
      window.location.href = `tel:${member.phoneNumber}`;
    }
  };

  const handleEmail = () => {
    if (member.email) {
      window.location.href = `mailto:${member.email}`;
    }
  };

  return (
    <div className="flex items-center gap-4">
      {/* Avatar */}
      <div className="w-14 h-14 rounded-full bg-aalaw-red flex items-center justify-center flex-shrink-0 shadow-md">
        <span className="text-xl font-bold text-white">{initials}</span>
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-900">{fullName}</p>
        <p className="text-sm text-gray-500">Inquiry Manager</p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {member.phoneNumber && (
          <button
            onClick={handleCall}
            className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            title={`Call ${fullName}`}
            aria-label={`Call ${fullName}`}
          >
            <span className="text-lg">📞</span>
          </button>
        )}
        {member.email && (
          <button
            onClick={handleEmail}
            className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            title={`Email ${fullName}`}
            aria-label={`Email ${fullName}`}
          >
            <span className="text-lg">✉️</span>
          </button>
        )}
      </div>
    </div>
  );
}
